import React from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
} from "@mui/material";
import {
  Search,
  LocalOffer,
  Store as StoreIcon,
  Close,
  Home,
  NavigateNext,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import MallHubSearchDropdown from "./MallHubSearchDropdown";

interface Mall {
  id?: string;
  name: string;
  location: string;
  city: string;
}

interface MallHubHeroProps {
  mall: Mall;
  totalOffers: number;
  totalStores: number;
  upcomingEventsCount: number;
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  showSearchResults: boolean;
  isSearching: boolean;
  hasSearchResults: boolean;
  debouncedSearchQuery: string;
  searchStores: any[];
  onCloseSearch: () => void;
  isSearchActive?: boolean;
}

/**
 * MallHubHero Component
 *
 * Hero section with breadcrumb navigation, mall info, and search
 */
const MallHubHero: React.FC<MallHubHeroProps> = ({
  mall,
  totalOffers,
  totalStores,
  upcomingEventsCount,
  searchQuery,
  onSearchChange,
  onClearSearch,
  showSearchResults,
  isSearching,
  hasSearchResults,
  debouncedSearchQuery,
  searchStores,
  onCloseSearch,
  isSearchActive = false,
}) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #1a237e 100%)", // Premium navy gradient
        color: "white",
        pt: { xs: 2, md: 4 },
        pb: { xs: 2.5, md: 5 },
        position: "relative",
        overflow: "visible",
        zIndex: isSearchActive ? 1200 : 1000,
        transition: "z-index 0s",
        boxShadow: "0 4px 20px rgba(26, 35, 126, 0.3)",
      }}
    >
      {/* Background Pattern - Premium overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.08,
          backgroundImage:
            "radial-gradient(circle, rgba(212, 175, 55, 0.4) 1px, transparent 1px)", // Gold accent dots
          backgroundSize: "24px 24px",
        }}
      />

      {/* Animated gradient overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.15) 0%, transparent 50%)",
          animation: "pulse 4s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 0.5 },
            "50%": { opacity: 1 },
          },
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Breadcrumb Navigation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: { xs: 1.5, md: 3 },
            }}
          >
            <Home
              sx={{ fontSize: 20, cursor: "pointer" }}
              onClick={() => navigate("/")}
            />
            <NavigateNext sx={{ fontSize: 20 }} />
            <Typography
              variant="body2"
              onClick={() => navigate("/explore")}
              sx={{
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Explore
            </Typography>
            <NavigateNext sx={{ fontSize: 20 }} />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {mall.name}
            </Typography>
          </Box>
        </motion.div>

        {/* Mall Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: "center", mb: { xs: 2, md: 4 } }}>
            <Typography
              variant="h2"
              fontWeight="bold"
              gutterBottom
              sx={{ fontSize: { xs: "2rem", md: "3rem" } }}
            >
              Welcome to {mall.name}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.95,
                mb: 1,
                fontSize: { xs: "1rem", md: "1.25rem" },
              }}
            >
              {mall.location}, {mall.city}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Chip
                icon={<LocalOffer />}
                label={`${totalOffers} Active Offer${
                  totalOffers !== 1 ? "s" : ""
                }`}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 600,
                  "& .MuiChip-icon": { color: "white" },
                }}
              />
              <Chip
                icon={<span style={{ fontSize: 16 }}>🎉</span>}
                label={`${upcomingEventsCount} Upcoming Event${
                  upcomingEventsCount > 1 ? "s" : ""
                }`}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 600,
                  "& .MuiChip-icon": { color: "white" },
                }}
              />
              <Chip
                icon={<StoreIcon />}
                label={`${totalStores} Stores`}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 600,
                  "& .MuiChip-icon": { color: "white" },
                }}
              />
            </Box>
          </Box>
        </motion.div>

        {/* Unified Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Paper
            elevation={8}
            sx={{
              maxWidth: { xs: "100%", sm: 700, md: 800 },
              mx: "auto",
              borderRadius: { xs: 3, md: 4 },
              overflow: "visible",
              position: "relative",
              zIndex: 1300,
            }}
          >
            <TextField
              fullWidth
              placeholder="Search for stores or brands..."
              value={searchQuery}
              onChange={onSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      sx={{
                        fontSize: { xs: 18, md: 22 },
                        color: "primary.main",
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={onClearSearch} size="small">
                      <Close sx={{ fontSize: { xs: 18, md: 20 } }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  fontSize: { xs: "0.813rem", md: "1rem" },
                  py: { xs: 0.5, md: 1.25 },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 4,
                  "& fieldset": { border: "none" },
                },
              }}
            />

            {/* Search Results Dropdown */}
            <AnimatePresence>
              {showSearchResults && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <MallHubSearchDropdown
                    isSearching={isSearching}
                    hasSearchResults={hasSearchResults}
                    debouncedSearchQuery={debouncedSearchQuery}
                    searchStores={searchStores}
                    mallName={mall.name}
                    searchQuery={searchQuery}
                    onClose={onCloseSearch}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default MallHubHero;
